/**
 * `FilterBar` の条件(`ConditionDef`)を組み立てる関数。日付範囲・複数選択。
 * 部品(FilterBar.tsx)とは別ファイル(Fast refresh の都合で、コンポーネント以外を同居させない)。
 */
import { DatePicker } from "antd";
import dayjs from "dayjs";

import { PopClose, type ConditionDef, type ConditionPreset } from "./FilterBar";
import { MultiSelectControl } from "./MultiSelectControl";

const DATE = "YYYY-MM-DD";

/**
 * 日付範囲の条件(`?from=&to=`)。値の表示は「2026-09-01 〜 2026-09-30」。
 * `onChange(from, to)` は空文字で解除。
 */
export function dateRangeCondition({
  key,
  label,
  from,
  to,
  onChange,
  preset,
}: {
  key: string;
  label: string;
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
  preset?: ConditionPreset[];
}): ConditionDef {
  const value = from || to ? `${from || "…"} 〜 ${to || "…"}` : "";
  return {
    key,
    label,
    value,
    preset,
    onClear: () => onChange("", ""),
    control: (close) => (
      <>
        <DatePicker.RangePicker
          allowEmpty={[true, true]}
          value={[from ? dayjs(from) : null, to ? dayjs(to) : null]}
          onChange={(v) => onChange(v?.[0]?.format(DATE) ?? "", v?.[1]?.format(DATE) ?? "")}
          placeholder={["開始", "終了"]}
        />
        <PopClose onClick={close} />
      </>
    ),
  };
}

/**
 * 複数選択の条件(`?kind=a,b`)。値の表示は「入社, 退社」(3 つ以上は「入社 ほか 2」)。
 */
export function multiSelectCondition({
  key,
  label,
  options,
  selected,
  onChange,
  preset,
}: {
  key: string;
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (values: string[]) => void;
  preset?: ConditionPreset[];
}): ConditionDef {
  const labels = selected.map((v) => options.find((o) => o.value === v)?.label ?? v);
  const value =
    labels.length === 0 ? "" : labels.length <= 2 ? labels.join(", ") : `${labels[0]} ほか ${labels.length - 1}`;
  return {
    key,
    label,
    value,
    preset,
    onClear: () => onChange([]),
    control: (close) => (
      <MultiSelectControl options={options} selected={selected} onChange={onChange} onClose={close} />
    ),
  };
}
